import { AlertCircle, Download } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/useAuth"
import {
  DownloadExpiredError,
  getFreshDownloadUrl,
  s3KeyFromGeneratedUrl,
} from "@/services/uploadService"

interface GeneratedImageProps {
  /** Presigned URL of a completed generated image (generated/…/*.png etc.). */
  href: string
  filename: string
}

/** Path (query stripped) ends with an image extension — render inline, not as a download card. */
export function isImageFilename(filename: string): boolean {
  return /\.(png|jpe?g|webp|gif)$/i.test(filename)
}

/**
 * Inline display for a COMPLETED generated image referenced by its direct URL.
 *
 * The assistant normally surfaces images via status.json URLs (PollingImage), but when it
 * writes a direct image URL (e.g. re-showing a page while editing / collecting pages for the
 * PDF), rendering a download card instead of the picture reads as a bug to the user. Mirror
 * PollingImage's "ready" presentation: the image itself with a small download link beneath.
 *
 * 署名付き URL は約1時間で失効する（2026-07-07 実走FB: 翌日の履歴で画像が壊れる）。
 * 表示エラー時に一度だけ、認証付き API で新しい署名付き URL に取り替える。
 * ダウンロードもクリック時にその場で新しい URL を取得する。
 */
export function GeneratedImage({ href, filename }: GeneratedImageProps) {
  const { token } = useAuth()
  const [src, setSrc] = useState(href)
  const [expired, setExpired] = useState(false)
  const refreshedRef = useRef(false)

  useEffect(() => {
    setSrc(href)
    setExpired(false)
    refreshedRef.current = false
  }, [href])

  const fileKey = s3KeyFromGeneratedUrl(href)

  const refreshSrc = async () => {
    if (refreshedRef.current || !fileKey || !token) return
    refreshedRef.current = true
    try {
      setSrc(await getFreshDownloadUrl(fileKey, token))
    } catch (err) {
      if (err instanceof DownloadExpiredError) setExpired(true)
    }
  }

  const handleDownload = async (e: React.MouseEvent) => {
    if (!fileKey || !token) return
    e.preventDefault()
    try {
      const fresh = await getFreshDownloadUrl(fileKey, token)
      window.open(fresh, "_blank", "noopener,noreferrer")
    } catch (err) {
      if (err instanceof DownloadExpiredError) setExpired(true)
    }
  }

  if (expired) {
    return (
      <div className="flex items-center gap-2 my-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
        <AlertCircle size={18} className="flex-shrink-0" />
        <span className="text-sm">
          画像の保存期間（7日）を過ぎています。必要ならもう一度作成を頼んでください。
        </span>
      </div>
    )
  }

  return (
    <div className="my-2">
      <img
        src={src}
        alt={filename}
        onError={refreshSrc}
        className="max-w-full rounded-lg border border-gray-200 shadow-sm"
      />
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleDownload}
        className="inline-flex items-center gap-1 mt-1 text-xs text-gray-500 hover:text-blue-600"
        title={`${filename} をダウンロード`}
      >
        <Download size={14} /> ダウンロード
      </a>
    </div>
  )
}
